"use client";

import { useState, type MouseEvent } from "react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface IDCellProps {
  id: string;
  className?: string;
}

/** A long provider/record id as a compact chip: hover to read it in full, click to copy. */
export const IDCell = ({ id, className }: IDCellProps) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!id) {
    return <span className="text-sm text-muted-foreground">N/A</span>;
  }

  const shortId = id.length > 10 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id;

  const copyToClipboard = async (event: MouseEvent<HTMLButtonElement>) => {
    // The chip often sits in a clickable row; copying should not also open or close it.
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      toast.success("ID copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy ID");
    }
  };

  return (
    <TooltipProvider delayDuration={150}>
      {/* Radix closes the tooltip on click, so keep it open while it says "Copied". */}
      <Tooltip open={open || copied} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={copyToClipboard}
            aria-label={`Copy ID ${id}`}
            className={cn(
              "inline-flex w-fit max-w-full items-center rounded-full border border-border/60 bg-muted/40 px-2.5 py-1 font-mono text-xs tracking-tight text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              className
            )}
          >
            <span className="truncate">{shortId}</span>
          </button>
        </TooltipTrigger>
        {/* No copy icon in the chip: it held empty space while hidden, so the hint lives here. */}
        <TooltipContent side="top" className="max-w-[320px] text-xs">
          {copied ? (
            "Copied"
          ) : (
            <>
              <span className="block break-all font-mono">{id}</span>
              <span className="mt-0.5 block opacity-70">Click to copy</span>
            </>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
